// The feis's own page on Feis.link (MC2 ecbc86f7, direction B): what a
// parent wants to know, worked out from what the organiser filled in. Every
// section is optional - an empty field hides its section rather than showing
// a blank. Plain functions so the page and its tests share one source.
import moment from "moment";
import { formatDate } from "../../helper/formatDate";
import { lateEntriesEnd } from "../../helper/lateEntries";

const clean = (s) => (typeof s === "string" ? s.trim() : "");
// Stored days are UTC midnights; formatDate turns one into that calendar day
// at local midnight. A Date already converted is used as it is: converting
// it again would give the day before east of UTC.
const asDay = (d) => (d instanceof Date ? d : formatDate(d));
const day = (d) => (d ? asDay(d) : null);
const endOf = (d) => {
  const e = new Date(d);
  e.setHours(23, 59, 59, 999);
  return e;
};
const startOf = (d) => {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  return s;
};

export const eventDayOf = (event) => event?.feis_date || event?.exam_date || event?.eventDays?.[0] || null;
export const longDay = (d) => (d ? moment(asDay(d)).format("dddd, MMMM D, YYYY") : "");
export const midDay = (d) => (d ? moment(asDay(d)).format("ddd, MMM D") : "");
// "November 6–8, 2026", "October 30 – November 1, 2026" or one long day
export const dayRange = (days) => {
  const list = (Array.isArray(days) ? days : []).filter(Boolean).map((d) => moment(asDay(d)));
  if (list.length === 0) return "";
  const first = list[0];
  const last = list[list.length - 1];
  if (first.isSame(last, "day")) return first.format("dddd, MMMM D, YYYY");
  if (first.isSame(last, "month")) return `${first.format("MMMM D")}–${last.format("D, YYYY")}`;
  if (first.isSame(last, "year")) return `${first.format("MMMM D")} – ${last.format("MMMM D, YYYY")}`;
  return `${first.format("MMMM D, YYYY")} – ${last.format("MMMM D, YYYY")}`;
};

// An Oireachtas page opened from a link has no router state: its state from
// its own dates, as pageStateForEvent does for a feis.
export const oireachtasState = (event, now = new Date()) => {
  if (!event) return undefined;
  const close = event.entriesCloseDate ? formatDate(event.entriesCloseDate) : null;
  const days = (event.eventDays || []).filter(Boolean);
  const first = days.length ? formatDate(days[0]) : null;
  const last = days.length ? endOf(formatDate(days[days.length - 1])) : null;
  // results once the last day is over; the days themselves are "today"
  if (last && now > last) return "result";
  if (first && now >= first) return "today";
  if (close) {
    const end = new Date(close);
    end.setHours(23, 59, 59, 999);
    if (now <= end) return "open";
  }
  return "closed";
};

export const shortDay = (d) => (d ? moment(asDay(d)).format("MMM D") : "");

// whole days from today to a stored day (0 = today, negative = past)
export const daysUntil = (d, now = new Date()) => {
  if (!d) return null;
  return Math.round((startOf(asDay(d)) - startOf(now)) / 86400000);
};
export const inDays = (n) => (n === 0 ? "today" : n === 1 ? "tomorrow" : n > 1 ? `in ${n} days` : "");

export const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
};

// What is true about this event for this family right now: the one line at
// the top of the page and the date it counts down to.
// state: upcoming | open | late | closed | today | result
export const headline = (event, { state, entered = false, dancerCount = 0, kind = "feis", now = new Date() } = {}) => {
  if (!event) return null;
  const opening = day(event.opening_date);
  const closing = day(event.closing_date || event.entriesCloseDate);
  const eventDay = day(eventDayOf(event));
  const lateFee = Number(event?.fees?.[0]?.lateFeeAfterClosed?.additionalFee) || 0;
  const noun = kind === "grade" ? "exam" : kind === "oireachtas" ? "Oireachtas" : "feis";
  const you = entered && dancerCount > 0 ? `${dancerCount} ${dancerCount === 1 ? "dancer" : "dancers"} entered` : entered ? "You're entered" : "";
  // an Oireachtas on its days: no line, just its main buttons (Devin 2026-10-30)
  if (state === "today" && kind === "oireachtas") return { tone: "today", title: "", detail: you };
  if (state === "today") return { tone: "today", title: `The ${noun} is today`, detail: you };
  // a grade exam stays "closed" after its day; the day passing ends it too
  if (state === "closed" && eventDay && now > endOf(eventDay)) state = "result";
  if (state === "result") {
    return { tone: "done", title: kind === "grade" ? "This exam has finished" : "Results are in", detail: eventDay ? `The ${noun} was on ${longDay(eventDay)}.` : "" };
  }
  if (state === "upcoming") {
    const n = daysUntil(opening, now);
    return { tone: "soon", title: opening ? `Registration opens ${midDay(opening)}` : "Registration opens soon", detail: opening && n > 0 ? inDays(n) : "", countTo: opening };
  }
  if (state === "open" || state === "late") {
    const n = daysUntil(closing, now);
    const late = state === "late" || (closing && now > endOf(closing));
    if (late) {
      // closed, and says so, but still taking late entries until the late
      // entries end day (MC2 5a45d7e3; an Oireachtas reads its own late close)
      const lateEnd = day(event.lateEntryCloseDate || (kind !== "oireachtas" ? lateEntriesEnd(event) : null));
      return {
        tone: "late",
        title: lateEnd ? `Late entries are open until ${midDay(lateEnd)}` : "Late entries are open",
        detail: [you, closing ? `Registration closed ${midDay(closing)}` : "", lateFee ? `A ${money(lateFee)} late fee applies.` : ""].filter(Boolean).join(" · "),
        countTo: lateEnd || undefined,
      };
    }
    return {
      tone: n !== null && n <= 3 ? "urgent" : "open",
      title: entered ? `${you}. You can add more until ${midDay(closing)}` : `Registration is open until ${midDay(closing)}`,
      detail: [n !== null ? `Closes ${inDays(n)}` : "", lateFee ? `${money(lateFee)} late fee after that` : ""].filter(Boolean).join(" · "),
      countTo: closing,
    };
  }
  // closed, not yet the day
  const n = daysUntil(eventDay, now);
  return {
    tone: "closed",
    title: entered ? `${you}. See you on ${midDay(eventDay)}` : "Registration has closed",
    detail: [eventDay && n > 0 ? `The ${noun} is ${inDays(n)}` : "", closing ? `Entries closed ${shortDay(closing)}` : ""].filter(Boolean).join(" · "),
    countTo: eventDay,
  };
};

// "1201 Boulevard René-Lévesque Ouest, Montréal, Québec H3B 2L7", without
// repeating parts the organiser already typed into the street field.
export const addressLine = (event) => {
  const street = clean(event?.venue) || clean(event?.venue_address);
  const lower = street.toLowerCase();
  const has = (part) => part && lower.includes(part.toLowerCase());
  const city = clean(event?.venue_city);
  const state = clean(event?.state);
  const postal = clean(event?.postal_code);
  if (street && has(postal)) return street;
  const parts = [street];
  if (city && !has(city)) parts.push(city);
  const tail = [state && !has(state) ? state : "", postal && !has(postal) ? postal : ""].filter(Boolean).join(" ");
  if (tail) parts.push(tail);
  return parts.filter(Boolean).join(", ");
};
export const placeLine = (event) => [clean(event?.venue_city), clean(event?.state)].filter(Boolean).join(", ");

export const coordsOf = (place) => {
  const lat = parseFloat(place?.latitude);
  const lng = parseFloat(place?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};
export const isAppleDevice = (nav = typeof navigator !== "undefined" ? navigator : undefined) =>
  /iPhone|iPad|iPod|Macintosh/.test(nav?.userAgent || "");
export const directionsUrl = (place, address, apple = isAppleDevice()) => {
  const c = coordsOf(place);
  const target = c ? `${c.lat},${c.lng}` : clean(address);
  if (!target) return null;
  return apple
    ? `https://maps.apple.com/?daddr=${encodeURIComponent(target)}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(target)}`;
};

// ---------------------------------------------------------------------------
// Competitions
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Fees: what the organiser set, in words a parent reads once.
// ---------------------------------------------------------------------------
export const feeLines = (event, { now = new Date() } = {}) => {
  const f = event?.fees?.[0] || {};
  const lines = [];
  const family = Number(f.familyfee) || 0;
  if (family > 0) lines.push({ key: "family", label: "Family fee", value: money(family), note: "once per family" });
  const early = Number(f?.earlyFee?.familyLess) || 0;
  const earlyBy = f?.earlyFee?.ifDateIsBefore;
  if (early > 0 && earlyBy && now <= endOf(day(earlyBy))) {
    lines.push({ key: "early", label: "Early bird", value: `−${money(early)}`, note: `off the family fee until ${shortDay(earlyBy)}` });
  }
  const lateFam = Number(f?.lateFee?.additionalFee) || 0;
  if (lateFam > 0 && f?.lateFee?.afterDate) lines.push({ key: "latefam", label: "Late family fee", value: `+${money(lateFam)}`, note: `from ${shortDay(f.lateFee.afterDate)}` });
  const late = Number(f?.lateFeeAfterClosed?.additionalFee) || 0;
  if (late > 0) lines.push({ key: "late", label: "Late entry", value: `+${money(late)}`, note: f?.lateFeeAfterClosed?.afterDate ? `after ${shortDay(f.lateFeeAfterClosed.afterDate)}` : "after registration closes" });
  // a family maximum the organiser really set (0 or a placeholder like $20,000 is none)
  const max = Number(f.familymax) || 0;
  if (max > 0 && max < 5000) lines.push({ key: "max", label: "Family maximum", value: money(max), note: "the most one family pays in entry fees" });
  return lines;
};

// The dates a family plans around.
export const keyDates = (event, { entered = false, kind = "feis", now = new Date() } = {}) => {
  if (!event) return [];
  const rows = [];
  const opening = day(event.opening_date);
  const closing = day(event.closing_date || event.entriesCloseDate);
  const eventDay = day(eventDayOf(event));
  const early = event?.fees?.[0]?.earlyFee;
  if (opening) rows.push({ key: "opens", label: "Registration opens", date: opening, past: now >= opening });
  if (early?.ifDateIsBefore && Number(early.familyLess) > 0) rows.push({ key: "early", label: "Early bird ends", date: day(early.ifDateIsBefore), past: now > endOf(day(early.ifDateIsBefore)) });
  if (closing) rows.push({ key: "closes", label: "Registration closes", date: closing, past: now > endOf(closing) });
  // late entries after the close, through the late entries end day (feis/grade)
  const lateEnd = kind !== "oireachtas" ? day(lateEntriesEnd(event)) : null;
  if (closing && lateEnd && lateEnd > closing) rows.push({ key: "late", label: "Late entries end", date: lateEnd, past: now > endOf(lateEnd) });
  if (eventDay) rows.push({ key: "day", label: { feis: "Feis day", grade: "Exam day", oireachtas: "Oireachtas begins" }[kind], date: eventDay, past: now > endOf(eventDay), main: true });
  return rows.sort((a, b) => a.date - b.date);
};

// Adjudicators, musicians, sponsors (or examiners), named, empty groups dropped.
export const peopleGroups = (collaborators, kind = "feis") => {
  const name = (p) => clean(p?.name) || [clean(p?.fname), clean(p?.lname)].filter(Boolean).join(" ");
  const groups =
    kind === "grade"
      ? [["Examiners", collaborators?.examiners], ["Sponsors", collaborators?.sponsors]]
      : [["Adjudicators", collaborators?.adjudicators], ["Musicians", collaborators?.musicians], ["Sponsors", collaborators?.sponsors]];
  return groups
    .map(([title, list]) => ({
      title,
      people: (Array.isArray(list) ? list : [])
        .map((p) => ({ id: p?._id, name: name(p), photo: p?.photo || "", location: clean(p?.location) }))
        .filter((p) => p.name),
    }))
    .filter((g) => g.people.length > 0);
};
// A flag for the country an adjudicator or musician comes from. The country
// is the part after the last comma ("Mid-Atlantic, USA", "Connacht, Ireland"),
// else the whole location, where the judge directory also names a country's
// own regions ("Southern England", "Western Australia"; Devin 2026-10-28:
// only Scotland's flag showed). "New England" alone is not England.
const FLAGS = [
  [/^(canada|ca)$/i, "🇨🇦"], [/^(usa|us|u\.s\.a?\.?|united states( of america)?|america)$/i, "🇺🇸"],
  [/^(ireland|eire|éire)$/i, "🇮🇪"], [/^northern ireland$/i, "🇬🇧"], [/^(uk|united kingdom|great britain|britain)$/i, "🇬🇧"],
  [/^(?!new )(.+ )?england$/i, "🏴󠁧󠁢󠁥󠁮󠁧󠁿"], [/^scotland$/i, "🏴󠁧󠁢󠁳󠁣󠁴󠁿"], [/^wales$/i, "🏴󠁧󠁢󠁷󠁬󠁳󠁿"],
  [/^((.+ )?australia|australian capital territory)$/i, "🇦🇺"], [/^new zealand$/i, "🇳🇿"],
  [/^south africa$/i, "🇿🇦"], [/^france$/i, "🇫🇷"], [/^germany$/i, "🇩🇪"], [/^italy$/i, "🇮🇹"], [/^spain$/i, "🇪🇸"],
  [/^norway$/i, "🇳🇴"], [/^romania$/i, "🇷🇴"], [/^japan$/i, "🇯🇵"], [/^taiwan$/i, "🇹🇼"], [/^hong kong$/i, "🇭🇰"],
  [/^(uae|united arab emirates)$/i, "🇦🇪"],
];
export const flagOf = (location) => {
  const country = clean(clean(location).split(",").pop());
  return (FLAGS.find(([re]) => re.test(country)) || [])[1] || "";
};

export const initialsOf = (name) =>
  clean(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

// The organiser's description as paragraphs; a line that is only a heading
// ("PARKING:", "Doors open") is kept as its own paragraph.
export const paragraphs = (text) =>
  clean(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

// Links and emails inside the organiser's text, so they can be tapped.
export const linkParts = (text) => {
  const re = /(https?:\/\/[^\s)]+|www\.[^\s)]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    const raw = m[0].replace(/[.,;:]+$/, "");
    const href = raw.includes("@") && !raw.startsWith("http") ? `mailto:${raw}` : raw.startsWith("http") ? raw : `https://${raw}`;
    out.push({ text: raw, href });
    last = m.index + raw.length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};
